import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CalendarDays, CalendarClock, Dog, ClipboardCheck, Percent, AlertTriangle,
  CalendarCheck, StickyNote, MapPin, Clock, ArrowRight,
} from "lucide-react";

const KPIS = [
  { k: "classes_today", label: "Classes Today", icon: CalendarDays },
  { k: "upcoming_classes", label: "Upcoming Classes", icon: CalendarClock },
  { k: "assigned_dogs", label: "Assigned Dogs", icon: Dog },
  { k: "homework_to_review", label: "Homework to Review", icon: ClipboardCheck },
  { k: "attendance_rate", label: "Attendance Rate", icon: Percent, suffix: "%" },
  { k: "dogs_needing_attention", label: "Dogs Needing Attention", icon: AlertTriangle },
  { k: "completed_month", label: "Completed (month)", icon: CalendarCheck },
  { k: "notes_added", label: "Training Notes Added", icon: StickyNote },
];

export default function TrainerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/trainer/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  const k = data?.kpis || {};

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {KPIS.map((kpi) => (
          <Card key={kpi.k} className="border-border" data-testid={`trainer-kpi-${kpi.k}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <kpi.icon size={15} strokeWidth={1.5} />
                <span className="text-[11px] uppercase tracking-wide font-semibold">{kpi.label}</span>
              </div>
              <div className="text-2xl font-heading font-semibold tracking-tight">{data ? k[kpi.k] : "—"}{kpi.suffix && data ? kpi.suffix : ""}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2"><Clock size={18} strokeWidth={1.5} /> Today's Classes</CardTitle>
          <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={() => navigate("/classes")} data-testid="trainer-view-classes">All classes <ArrowRight size={14} /></Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(data?.classes_today?.length ? data.classes_today : data?.upcoming || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between p-4 rounded-lg border border-border/60 hover:bg-muted/40 transition-colors" data-testid="trainer-today-class">
              <div>
                <div className="font-medium">{c.name}</div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                  <span className="flex items-center gap-1"><Clock size={12} /> {c.time || "—"}</span>
                  <span className="flex items-center gap-1"><MapPin size={12} /> {c.location_name}</span>
                  <span className="flex items-center gap-1"><Dog size={12} /> {c.enrolled_count} dogs</span>
                  <span>{c.program_name}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge value={c.status} />
                <Button size="sm" onClick={() => navigate(`/classes/${c.id}`)} data-testid="trainer-open-class">Open class</Button>
              </div>
            </div>
          ))}
          {!data?.classes_today?.length && !data?.upcoming?.length && (
            <p className="text-sm text-muted-foreground py-6 text-center">No classes scheduled.</p>
          )}
          {!data?.classes_today?.length && data?.upcoming?.length > 0 && (
            <p className="text-xs text-muted-foreground text-center">No classes today — showing your upcoming classes.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
